import type {
  FetchScheduleNotesHistoryArgs,
  FetchOrgScheduleNotesArgs,
  CreateScheduleNoteArgs,
  FetchScheduleNotesArgs,
  UpdateScheduleNoteArgs,
} from './types/scheduleNote.types';
import type {
  ApiScheduleNotesHistoryPage,
  UiScheduleNoteHistory,
  ApiScheduleNote,
  UiScheduleNote,
} from '../schemas/scheduleNote.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  mapScheduleNoteHistoryFromApi,
  updateScheduleNoteApiArgs,
  createScheduleNoteApiArgs,
  mapScheduleNoteFromApi,
} from './mappers/scheduleNote.mappers';
import { destroy, patch, post, get } from './axios';

// T0b — the organization is the SCOPE of these two single-client reads, and
// the route parameter is where it is authoritative: the `:organizationId`
// mount is what the backend narrows the query by (and what its guard checks).
// Both args types and both query keys already carried `organizationId`; only
// the URLs did not, so an organization client profile listed that client's
// notes from every other organization and from personal scope.
const scheduleNotesBase = (organizationId?: string) =>
  organizationId
    ? `/v2/organizations/${organizationId}/schedule-notes`
    : '/v2/schedule-notes';

export const createScheduleNote = async (args: CreateScheduleNoteArgs) => {
  // S1 — the backend takes a create's scope from the MOUNT and refuses an
  // organization that only appears in the body, so an organization note has to
  // be posted to the organization route. The body still echoes the same id
  // during the compatibility window; the backend accepts it only when it
  // agrees with the route.
  const response = await post<ApiScheduleNote[]>(
    scheduleNotesBase(args.organizationId),
    createScheduleNoteApiArgs(args),
  );
  return response?.data ?? null;
};

export const fetchScheduleNotes = async ({
  clientId,
  from,
  organizationId,
  to,
}: FetchScheduleNotesArgs) => {
  const response = await get<BaseApiResponse<ApiScheduleNote[]>>(
    `${scheduleNotesBase(organizationId)}?clientId=${clientId}&from=${from}&to=${to}`,
  );
  return {
    ...response,
    data: response?.data?.map(mapScheduleNoteFromApi) as UiScheduleNote[],
  };
};

export const fetchOrgScheduleNotes = async ({
  from,
  organizationId,
  to,
}: FetchOrgScheduleNotesArgs) => {
  const response = await get<BaseApiResponse<ApiScheduleNote[]>>(
    `/v2/organizations/${organizationId}/schedule-notes?from=${from}&to=${to}`,
  );
  return {
    ...response,
    data: response?.data?.map(mapScheduleNoteFromApi) as UiScheduleNote[],
  };
};

export const fetchScheduleNotesHistory = async ({
  clientId,
  limit = 20,
  organizationId,
  page,
}: FetchScheduleNotesHistoryArgs) => {
  const response = await get<BaseApiResponse<ApiScheduleNotesHistoryPage>>(
    `${scheduleNotesBase(organizationId)}/history?clientId=${clientId}&page=${page}&limit=${limit}`,
  );
  return {
    data: (response?.data?.items ?? []).map(
      mapScheduleNoteHistoryFromApi,
    ) as UiScheduleNoteHistory[],
    total: response?.data?.total ?? 0,
  };
};

export const updateScheduleNote = async ({
  id,
  ...rest
}: UpdateScheduleNoteArgs) => {
  const response = await patch<ApiScheduleNote>(
    `/v2/schedule-notes/${id}`,
    updateScheduleNoteApiArgs(rest),
  );
  return response?.data ?? null;
};

export const deleteScheduleNote = async (id: string) => {
  await destroy(`/v2/schedule-notes/${id}`);
};

export const deleteFutureScheduleNotes = async (id: string) => {
  await destroy(`/v2/schedule-notes/${id}/future-series`);
};
